import { FC, useState } from "react";
import { Transaction, TransactionRow, TransactionStatus } from "../model";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import moment from "moment";
import Table from "../../../assets/components/Table/Table";
import { Products } from "../../../models/models";
import Checkbox from "../../../assets/components/Checkbox/Checkbox";
import TableColumnVisibilityMenu from "../../../assets/components/Table/TableColumnVisibilityMenu";

const getTransactionStatusKey = (
    value: number,
): keyof typeof TransactionStatus => {
    return TransactionStatus[value] as keyof typeof TransactionStatus;
};

const getTransactionStatusColor = (status: TransactionStatus): string => {
    switch (status) {
        case TransactionStatus.Completed:
            return "#4CAF50";
        case TransactionStatus.Pending:
            return "#FF9800";
        case TransactionStatus.Failed:
            return "#F44336";
        case TransactionStatus.Refunded:
            return "#9E9E9E";
        default:
            return "#000000";
    }
};

interface TransactionsTableProps {
    transactions: Transaction[];
    products: Products;
    selectedIds?: number[];
    onSelectAll?: (checked: boolean) => void;
    onSelectRow?: (id: number, checked: boolean) => void;
}

const TransactionsTable: FC<TransactionsTableProps> = ({
    transactions,
    products,
    selectedIds = [],
    onSelectAll,
    onSelectRow,
}) => {
    // Default visibility configuration - hide some columns by default if needed
    const defaultColumnVisibility: VisibilityState = {
        // All columns visible by default
        // You can set specific columns to false to hide them by default
        // Example: "Created At": false,
    };

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        defaultColumnVisibility,
    );

    const handleResetColumnVisibility = () => {
        setColumnVisibility(defaultColumnVisibility);
    };

    const columns: ColumnDef<TransactionRow>[] = [
        {
            id: "orderId",
            header: "Order ID",
            accessorFn: ({ orderId }) => orderId,
            cell: ({ row }: any) => {
                const isFailed =
                    row.original.status === TransactionStatus.Failed;

                if (!onSelectAll || !onSelectRow) {
                    return row.original.orderId;
                }

                return (
                    <div onClick={isFailed ? (e) => e.stopPropagation() : undefined}>
                        <Checkbox
                            name={`select-${row.original.id}`}
                            value={String(row.original.id)}
                            label={row.original.orderId}
                            checked={isFailed ? selectedIds.includes(row.original.id) : false}
                            disabled={!isFailed}
                            onChange={isFailed ? (e) => {
                                onSelectRow(row.original.id, e.target.checked);
                                return e;
                            } : undefined}
                            useEllipsis
                        />
                    </div>
                );
            },
            size: 180,
        },
        {
            id: "transactionId",
            header: "Transactions ID",
            accessorFn: ({ transactionId }) => transactionId,
        },
        {
            id: "productName",
            header: "Product Name",
            accessorFn: ({ productTitle }) => productTitle,
        },
        {
            id: "status",
            header: "Status",
            accessorFn: ({ status }) => getTransactionStatusKey(status),
            cell: ({ row }) => (
                <span
                    style={{
                        color: getTransactionStatusColor(row.original.status),
                        fontWeight: 600,
                    }}
                >
                    {getTransactionStatusKey(row.original.status)}
                </span>
            ),
        },
        {
            id: "createDate",
            header: "Create Date",
            accessorFn: ({ createdAt }) => moment(createdAt).format("LLL"),
        },
    ];

    return (
        <Table<TransactionRow>
            borderRadius="small"
            data={transactions}
            columns={columns}
            getRowCanExpand={() => false}
            enableColumnVisibility={true}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            renderTopToolbar={(table) => (
                <TableColumnVisibilityMenu
                    table={table}
                    onReset={handleResetColumnVisibility}
                />
            )}
        />
    );
};

export default TransactionsTable;
